import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { LayoutLibrary } from '../..'
import { SizeTracker } from '../../../common/SizeTracker'
import { Loading } from '../../../common/loading'
import { Table } from '../../../common/table'
import { debounce } from '../../../helpers/common'
import { COMPONENTS } from '../../../routers/constants'
import { OpenNewTabComponent } from '../../common/openNewTab'
import { NewTab } from '../portfoliosOverview/components/NewTab'
import { PaginationTableOverview } from './components/PaginationTable'
import { PORTFOLIO_VALUES } from './constant'
import { HeaderPortfoliosOverview } from './headerPortfoliosOverview'
import { Header } from './headerPortfoliosOverview/Header'
import { getSchemaTablePortfolios } from './schemaTableSavedPortfolio'
import {
  changeFilter,
  resetStore,
  selectCheckboxPortfolio,
  selectDataPageTableSavedPortfolio,
  selectDataTable,
  selectFilter,
  selectIDCompanySearch,
  selectIds,
  selectIsLoadingTableSavedPortfolio,
  selectIsLoadingTypeAlert,
  selectTypeAlert,
} from './store/slice/slice'
import {
  clearStoreComponent,
  selectFragment,
  selectNameCompanySearch,
  selectTypePortfolioSelect,
} from './store/slice/sliceComponent'
import {
  getAlertProfile,
  getHeaderSavedPortfolio,
  getIdPortfolioAllByOrg,
  getSavedPortfolioByOrg,
  getSearchPortfolio,
  getSearchPortfolioAll,
} from './store/thunk'

const SavedPortfolios = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const ids = useSelector(selectIds)

  const isFragment = useSelector(selectFragment)
  const isLoadingTable = useSelector(selectIsLoadingTableSavedPortfolio)
  const isLoadingTypeAlert = useSelector(selectIsLoadingTypeAlert)
  const typeAlertPortfolio = useSelector(selectTypeAlert)
  const { page, pageSize } = useSelector(selectDataPageTableSavedPortfolio)
  const filter = useSelector(selectFilter)
  const typeSearch = useSelector(selectTypePortfolioSelect)
  const textSearch = useSelector(selectNameCompanySearch)
  const idSearch = useSelector(selectIDCompanySearch)
  const locale = useSelector((state) => state.i18n.locale)
  const listCheckbox = useSelector(selectCheckboxPortfolio(['listIdsSelected']))
  const isCanNavigate = !Object.values(listCheckbox).some(Boolean)

  const [position, setPosition] = useState({})
  const [showNewTab, setShowNewTab] = useState(false)
  const [isShowRenameOnly, setIsShowRenameOnly] = useState(false)
  const [id, setId] = useState()

  const tableRef = useRef()

  const onRowClick = (id) => {
    if (isCanNavigate && !isShowRenameOnly) {
      navigate(
        `${COMPONENTS.library.components.savedPortfolios.components.detailsPortfolio.route}?id=${id}`,
      )
    }
  }

  const onRightClick = (e, rowId) => {
    e.preventDefault()
    setPosition({
      x: e.pageX,
      y: e.clientY,
    })
    setShowNewTab(true)
    setId(rowId)
  }

  const handleChangeSort = (sort) => {
    dispatch(changeFilter({ ...sort, page: 1 }))
  }

  const getAllIdPort = useCallback(
    debounce(
      (value) =>
        dispatch(
          getSearchPortfolioAll({
            InputSearch: value,
          }),
        ),
      300,
    ),
    [],
  )

  useEffect(() => {
    if (
      typeSearch.value === PORTFOLIO_VALUES.PORTFOLIO ||
      (typeSearch.value === PORTFOLIO_VALUES.COMPANY && !idSearch)
    ) {
      dispatch(
        getSearchPortfolio({
          InputSearch: textSearch,
          Page: page,
          PageSize: pageSize,
          Sort: filter?.sortBy,
          SortDirection: filter?.sortDirection,
        }),
      )
    }

    if (typeSearch.value === PORTFOLIO_VALUES.COMPANY && !!idSearch) {
      dispatch(
        getSavedPortfolioByOrg({
          Page: page,
          PageSize: pageSize,
          OrganizationId: idSearch,
          Sort: filter?.sortBy,
          SortDirection: filter?.sortDirection,
        }),
      )
    }
  }, [
    page,
    isFragment,
    filter?.sortBy,
    filter?.sortDirection,
    locale,
    pageSize,
  ])

  useEffect(() => {
    if (
      typeSearch.value === PORTFOLIO_VALUES.PORTFOLIO ||
      (typeSearch.value === PORTFOLIO_VALUES.COMPANY && !idSearch)
    ) {
      getAllIdPort(textSearch)
    }

    if (typeSearch.value === PORTFOLIO_VALUES.COMPANY && !!idSearch) {
      dispatch(
        getIdPortfolioAllByOrg({
          OrganizationId: idSearch,
        }),
      )
    }
  }, [typeSearch, textSearch, idSearch])

  useEffect(() => {
    dispatch(getAlertProfile())
    dispatch(getHeaderSavedPortfolio())
  }, [locale])

  useEffect(() => {
    return () => {
      dispatch(resetStore())
      dispatch(clearStoreComponent())
    }
  }, [])

  return (
    <>
      <LayoutLibrary
        renderTopComponent={() => (
          <div style={{ padding: '0 6px' }}>
            <Header />
          </div>
        )}
        renderBottomComponent={() => (
          <div
            className="flexColumn h100 px1"
            style={{ padding: '0 6px', paddingTop: 12 }}
          >
            <SizeTracker
              renderTopComponent={() => <HeaderPortfoliosOverview />}
            >
              {(size) => {
                return (
                  <div
                    ref={tableRef}
                    className="positionRelative"
                    id="sideBarPortfolio"
                    style={{
                      opacity: isLoadingTable ? 0 : 1,
                      height: `calc(100% - ${size.height}px)`,
                    }}
                  >
                    <Table
                      keyReCalculateSize={size.height}
                      getDataFromRedux={selectDataTable}
                      ids={ids}
                      noDataText="library.main.NODATA_SAVE_PORTFOLIO"
                      isNoDataWithHead={true}
                      onRowClick={onRowClick}
                      onRightClick={onRightClick}
                      schema={getSchemaTablePortfolios(
                        typeAlertPortfolio,
                        isShowRenameOnly,
                        setIsShowRenameOnly,
                      )}
                      renderPagination={() => <PaginationTableOverview />}
                      sort={{
                        sortBy: filter.sortBy,
                        sortDirection: filter.sortDirection,
                      }}
                      changeSort={handleChangeSort}
                      isCenterNoData={false}
                    />
                  </div>
                )
              }}
            </SizeTracker>
            {(isLoadingTable || isLoadingTypeAlert) && <Loading />}
          </div>
        )}
      />

      {showNewTab && (
        <OpenNewTabComponent
          y={position?.y}
          x={position?.x}
          handleClickOutside={() => setShowNewTab(false)}
          parentRef={tableRef}
        >
          <NewTab
            linkNewTab={`${COMPONENTS.library.components.savedPortfolios.components.detailsPortfolio.route}?id=${id}`}
          />
        </OpenNewTabComponent>
      )}
    </>
  )
}

export default SavedPortfolios
